import { computed, ref, type Ref } from 'vue'

import type { FleetRun } from '@/services/claude-fleet'
import { isNothingToDoRun } from '@/services/claude-fleet/claude-fleet.runs'

const SHOW_SKIPPED_KEY = 'adx-claude-fleet-runs-show-skipped'

function readShowSkipped(): boolean {
  try {
    return window.localStorage.getItem(SHOW_SKIPPED_KEY) === '1'
  } catch {
    return false
  }
}

/**
 * The Runs table's Show skipped choice: runs that found nothing to do are left out of `filteredRuns` unless the
 * viewer shows them or filters on the Skipped outcome. Each viewer's choice is kept in this browser; storage that
 * is blocked just means the default each visit.
 */
export function useFleetRunsSkipped(filteredRuns: Ref<FleetRun[]>, outcome: Ref<string | null | undefined>) {
  const showSkipped = ref(readShowSkipped())

  function setShowSkipped(show: boolean) {
    showSkipped.value = show
    try {
      window.localStorage.setItem(SHOW_SKIPPED_KEY, show ? '1' : '0')
    } catch {
      // Storage blocked: the choice lasts until the page reloads.
    }
  }

  const skippedHidden = computed(() => !showSkipped.value && outcome.value !== 'skipped')

  /** The number of nothing-to-do runs on this page, shown on the Show skipped chip whether or not they are hidden. */
  const skippedCount = computed(() => filteredRuns.value.filter(isNothingToDoRun).length)

  const hiddenSkippedCount = computed(() => (skippedHidden.value ? skippedCount.value : 0))

  const visibleRuns = computed(() =>
    skippedHidden.value ? filteredRuns.value.filter(run => !isNothingToDoRun(run)) : filteredRuns.value
  )

  return { showSkipped, setShowSkipped, skippedCount, hiddenSkippedCount, visibleRuns }
}
